import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, GitCompare, X, Eye, CalendarX, Trophy, List } from 'lucide-react';
import ImageModal from './ImageModal';
import BuyboxModal from './BuyboxModal';
import api from '../utils/api';

interface ProductCardProps {
  product: any;
  onAddToComparison?: (product: any) => void;
  onRemoveFromComparison?: (productId: string) => void;
  comparisonProducts?: any[];
  isInComparison?: boolean;
  precioReferencia?: number;
  comparisonMode?: boolean;
  currentTableProducts?: any[]; // Productos disponibles en la toma actual
  isFromSavedGroup?: boolean; // Indica si este producto viene de un grupo guardado
  currentTableInfo?: { name: string; date?: string }; // Información de la toma actual
  tableName?: string; // Nombre de la tabla actual para cargar variantes del buybox
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onAddToComparison,
  onRemoveFromComparison,
  comparisonProducts = [],
  isInComparison = false,
  precioReferencia,
  comparisonMode = false,
  currentTableProducts = [],
  isFromSavedGroup = false,
  currentTableInfo,
  tableName
}) => {
  const { t } = useTranslation();

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ url: string; title: string } | null>(null);
  const [buyboxModalOpen, setBuyboxModalOpen] = useState(false);
  const [buyboxVariantsCount, setBuyboxVariantsCount] = useState<number>(
    product.buyboxVariantsCount || 0
  );

  // Extraer información del producto
  const getProductImage = () => {
    return product.imagen || product.image || product.IMAGEN || product.Imagen || product.IMAGE || '';
  };

  const getProductTitle = () => {
    return product.title || product.titulo || product.TITULO || product.Titulo || product.nombre || product.NOMBRE || 'Producto sin título';
  };

  const getProductPrice = () => {
    // Obtener el precio original (sin descuento)
    // Los campos son DECIMAL(10,2), ya están en pesos (44999.00 = $44.999)
    let priceValue = product.original_price || product.precio || product.PRECIO || product.Precio || product.sale_price || product.precio_venta || product.PRECIO_VENTA || 0;
    
    // Si es número, usarlo directamente, si es string parsearlo
    const parsed = typeof priceValue === 'number' ? priceValue : parseFloat(String(priceValue));
    return isNaN(parsed) ? 0 : parsed;
  };

  const getProductDiscountPrice = () => {
    // Primero verificar si hay un precio con descuento explícito
    let discountValue = null;
    
    if (product.discounted_price && product.discounted_price !== "null") {
      discountValue = product.discounted_price;
    } else if (product.precio_desc || product.PRECIO_DESC || product.precio_descuento) {
      discountValue = product.precio_desc || product.PRECIO_DESC || product.precio_descuento;
    } else if (product.sale_price && product.original_price) {
      // Si no hay descuento explícito, pero sale_price < original_price, usar sale_price como precio con descuento
      // Los campos son DECIMAL(10,2), ya están en pesos
      const salePrice = typeof product.sale_price === 'number' ? product.sale_price : parseFloat(String(product.sale_price));
      const originalPrice = typeof product.original_price === 'number' ? product.original_price : parseFloat(String(product.original_price));
      
      if (!isNaN(salePrice) && !isNaN(originalPrice) && salePrice < originalPrice) {
        return salePrice; // Usar directamente sin dividir
      }
    }
    
    if (!discountValue) return null;
    
    // Los campos son DECIMAL(10,2), ya están en pesos
    const parsed = typeof discountValue === 'number' ? discountValue : parseFloat(String(discountValue));
    return isNaN(parsed) || parsed === 0 ? null : parsed;
  };

  const getProductBrand = () => {
    return product.Marca || product.marca || product.MARCA || 'Sin marca';
  };

  const getProductSite = () => {
    // Retornar el valor exacto de la base de datos sin procesar
    return product.sitio || product.SITIO || product.Sitio || '';
  };

  const getProductCategory = () => {
    return product.Cat || product.categoria || product.CATEGORIA || product.Categoria || '';
  };

  const getProductUrl = () => {
    return product.url || product.URL || product.Url || '';
  };

  // Parsear apariciones para detectar múltiples posiciones
  const parseApariciones = () => {
    if (!product.apariciones) return [];
    try {
      let aparicionesData;
      if (typeof product.apariciones === 'string') {
        // Try to fix Python dict format to valid JSON
        let fixedJson = product.apariciones;
        
        // Check if it looks like Python dict format (has single quotes)
        if (fixedJson.includes("'")) {
          // Convert Python dict to valid JSON
          fixedJson = fixedJson
            .replace(/'/g, '"')  // Replace single quotes with double quotes
            .replace(/(\w+):/g, '"$1":')  // Add quotes to unquoted keys
            .replace(/"{2,}/g, '"')  // Clean up multiple quotes
            .replace(/"("?\w+"?)":/g, (match: string, key: string) => {
              // Clean up over-quoted keys
              const cleanKey = key.replace(/"/g, '');
              if (['page', 'orderInPage', 'category_toma'].includes(cleanKey)) {
                return `"${cleanKey}":`;
              }
              return `"${cleanKey}":`;
            });
        }
        
        aparicionesData = JSON.parse(fixedJson);
      } else {
        aparicionesData = product.apariciones;
      }
      return Array.isArray(aparicionesData) ? aparicionesData : [];
    } catch (e) {
      console.warn('Error parsing apariciones in ProductCard:', product.apariciones, e);
      return [];
    }
  };

  const apariciones = parseApariciones();
  const hasMultipleApariciones = apariciones.length > 1;

  const getProductPage = () => {
    // Si hay apariciones, usar la primera
    if (apariciones.length > 0) {
      return String(apariciones[0].page || 0).padStart(2, '0');
    }
    const page = product.page;
    return page ? String(page).padStart(2, '0') : '--';
  };

  const getProductOrder = () => {
    // Si hay apariciones, usar la primera
    if (apariciones.length > 0) {
      return String(apariciones[0].orderInPage || 0).padStart(2, '0');
    }
    const order = product.orderInPage || product.order;
    return order ? String(order).padStart(2, '0') : '--';
  };

  const getProductInstallments = () => {
    let cuotas = product.installements || product.cuotas || product.CUOTAS || product.Cuotas || '';
    let precioCuota = product.installements_value || product.precio_cuota || product.PRECIO_CUOTA || product.valor_cuota || '';
    
    // Filtrar valores null o 'null' como string
    if (cuotas === 'null' || cuotas === null) {
      cuotas = '';
    }
    if (precioCuota === 'null' || precioCuota === null) {
      precioCuota = '';
    }
    
    // Si no hay precio de cuota pero sí hay cuotas, calcular
    if (!precioCuota && cuotas && cuotas !== '') {
      const finalPrice = getProductDiscountPrice() || getProductPrice();
      const numCuotas = typeof cuotas === 'string' ? parseInt(cuotas) : cuotas;
      
      if (numCuotas > 0 && finalPrice > 0 && !isNaN(numCuotas)) {
        precioCuota = finalPrice / numCuotas;
      }
    }
    
    return { cuotas, precioCuota };
  };

  const price = getProductPrice();
  const discountPrice = getProductDiscountPrice();
  const hasDiscount = discountPrice && discountPrice > 0 && discountPrice !== price;
  const finalPrice = hasDiscount ? discountPrice : price;
  
  // Calcular porcentaje de descuento
  const rawDiscountPercentage = hasDiscount && price > 0 
    ? Math.round(((price - discountPrice) / price) * 100)
    : 0;
  
  // Si es negativo, los datos están mal (usar valor absoluto sin signo -)
  // Si es positivo, es descuento real (mostrar con signo -)
  const discountPercentage = Math.abs(rawDiscountPercentage);
  const isRealDiscount = rawDiscountPercentage > 0; // true = descuento real, false = error en datos
  

  

  
  // Determinar el color del precio basado en el precio de referencia
  const getPriceColor = () => {
    if (!precioReferencia) return 'text-gray-900';
    const priceInPesos = finalPrice; // Ya está en pesos, no dividir por 100
    if (priceInPesos < precioReferencia) return 'text-green-600';
    if (priceInPesos === precioReferencia) return 'text-cyan-600';
    return 'text-red-600';
  };

  // Abrir modal de imagen
  const openImageModal = (imageUrl: string, title: string) => {
    setSelectedImage({ url: imageUrl, title });
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
  };

  // Verificar si el producto está realmente en comparación usando la lista actual
  const checkIfInComparison = () => {
    // En modo comparación, siempre está en comparación
    if (comparisonMode) {
      return true;
    }
    
    if (!comparisonProducts || comparisonProducts.length === 0) {
      return false;
    }
    
    const isInComp = comparisonProducts.some(cp => {
      // Comparar usando comparisonId si existe
      if (product.comparisonId && cp.comparisonId) {
        return product.comparisonId === cp.comparisonId;
      }
      
      // Fallback: intentar coincidir por id o id_catalogo
      const cpId = cp.id || cp.id_catalogo || cp.ID_CATALOGO;
      const currentProductId = product.id || product.id_catalogo || product.ID_CATALOGO;
      const matches = cpId && currentProductId && String(cpId) === String(currentProductId);
      
      if (matches) {
      }
      return matches;
    });
    
    return isInComp;
  };
  
  // Usar la verificación actualizada en lugar del prop
  const isReallyInComparison = checkIfInComparison();
  
  // Manejar comparación
  const handleComparisonToggle = () => {
    
    if (isReallyInComparison && onRemoveFromComparison) {
      // Usar directamente comparisonId ya que es el identificador único en el sistema de comparación
      const productId = product.comparisonId;
      
      onRemoveFromComparison(productId);
    } else if (!isReallyInComparison && onAddToComparison) {
      // Incluir el buyboxVariantsCount actual al agregar a comparación
      const productWithBuyboxInfo = {
        ...product,
        buyboxVariantsCount: buyboxVariantsCount,
        originalTableName: product.originalTableName || tableName
      };
      onAddToComparison(productWithBuyboxInfo);
    } else {
    }
  };

  const { cuotas, precioCuota } = getProductInstallments();

  // Verificar si el producto tiene buybox usando los mismos campos que la tabla
  const rawBuyboxId = product.buybox_id;
  const rawBuyboxWinner = product.buybox_winner;
  
  
  // Verificar si buybox_id es válido (misma lógica que en DataTable)
  const isValidBuyboxId = rawBuyboxId !== null && 
                        rawBuyboxId !== undefined && 
                        rawBuyboxId !== '' && 
                        rawBuyboxId !== '(NULL)' && 
                        rawBuyboxId !== 'NULL' &&
                        rawBuyboxId !== '0' &&
                        rawBuyboxId !== 0;
  
  const buyboxId = isValidBuyboxId ? rawBuyboxId : null;
  
  // Verificar si es ganador del buybox
  const isBuyboxWinner = rawBuyboxWinner === 1 || 
                       rawBuyboxWinner === '1' || 
                       rawBuyboxWinner === true ||
                       rawBuyboxWinner === 'true' ||
                       parseInt(rawBuyboxWinner) === 1;
  
  // Validación adicional: solo considerar válidos los IDs numéricos
  const hasBuybox = buyboxId && /^\d+$/.test(String(buyboxId)) && isBuyboxWinner;
  const hasValidBuyboxId = buyboxId && /^\d+$/.test(String(buyboxId));
  
  // Cargar el número de variantes del buybox
  useEffect(() => {
    if (hasValidBuyboxId && buyboxId && buyboxVariantsCount === 0) {
      const tableToUse = product.originalTableName || tableName;
      
      if (tableToUse && tableToUse !== 'comparison') {
        api.get(`/api/tables/${tableToUse}/buybox/${buyboxId}`)
          .then(response => {
            const variantsCount = response.data.totalVariants || 
                                 response.data.variants?.length || 
                                 response.data.length || 
                                 1;
            setBuyboxVariantsCount(variantsCount);
          })
          .catch((error) => {
            console.error('Error loading buybox variants:', error.message);
            setBuyboxVariantsCount(1);
          });
      } else {
        // Si no hay tabla o es 'comparison', usar endpoint alternativo
        api.get(`/api/buybox/${buyboxId}/variants`)
          .then(response => {
            setBuyboxVariantsCount(response.data.totalVariants || response.data.variants?.length || 1);
          })
          .catch((error) => {
            console.error('Error loading buybox variants (alternate):', error.message);
            setBuyboxVariantsCount(1);
          });
      }
    }
  }, [hasValidBuyboxId, buyboxId, tableName, buyboxVariantsCount]);

  // Verificar si el producto está disponible en la toma actual
  const isAvailableInCurrentTable = () => {
    // Si no viene de un grupo guardado, siempre está disponible
    if (!isFromSavedGroup || !currentTableProducts || currentTableProducts.length === 0) {
      return true;
    }

    // 1) Coincidencia por id_catalogo / ID_CATALOGO / id (demo)
    const productIdCatalogo = product.id_catalogo || product.ID_CATALOGO || product.id;
    if (productIdCatalogo !== undefined && productIdCatalogo !== null && productIdCatalogo !== '') {
      const foundById = currentTableProducts.some((currentProduct) => {
        const currentId = currentProduct.id_catalogo || currentProduct.ID_CATALOGO || currentProduct.id;
        return currentId !== undefined && String(currentId) === String(productIdCatalogo);
      });
      if (foundById) return true;
    }

    // 2) Coincidencia por URL exacta si está disponible
    const productUrl = (product.url || product.URL || '').trim();
    if (productUrl) {
      const foundByUrl = currentTableProducts.some((currentProduct) => {
        const currentUrl = (currentProduct.url || currentProduct.URL || '').trim();
        return currentUrl !== '' && currentUrl === productUrl;
      });
      if (foundByUrl) return true;
    }

    // 3) Coincidencia por título y marca como fallback (tolerante si falta marca)
    const normalize = (text: string) =>
      text
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/\s+/g, ' ')
        .trim();

    const productTitle = normalize(getProductTitle());
    const productBrand = normalize(getProductBrand());

    const foundByTitleBrand = currentTableProducts.some((currentProduct) => {
      const currentTitle = normalize(
        currentProduct.titulo || currentProduct.title || currentProduct.TITULO || ''
      );
      const currentBrand = normalize(
        currentProduct.Marca || currentProduct.marca || currentProduct.MARCA || ''
      );

      // Si alguna de las marcas falta, comparar solo por título; si ambas existen, requerir también marca
      const bothBrandsPresent = productBrand !== '' && currentBrand !== '';
      return bothBrandsPresent
        ? currentTitle === productTitle && currentBrand === productBrand
        : currentTitle === productTitle;
    });

    return foundByTitleBrand;
  };

  // Usar useMemo para forzar re-evaluación cuando cambien las dependencias
  const isProductAvailable = useMemo(() => {
    return isAvailableInCurrentTable();
  }, [currentTableProducts, currentTableInfo, isFromSavedGroup]);

  // Generar mensaje del tooltip para productos no disponibles
  const getUnavailableTooltip = () => {
    if (currentTableInfo?.date) {
      return `${t('comparison_groups.not_available_in_current')} (${currentTableInfo.date})`;
    } else if (currentTableInfo?.name) {
      return `${t('comparison_groups.not_available_in_current')} - ${currentTableInfo.name}`;
    }
    return t('comparison_groups.not_available_in_current');
  };

  return (
    <>
      <div className={`bg-white rounded-lg shadow-sm border ${
        !isProductAvailable
          ? 'border-gray-400 bg-gray-50'
          : isReallyInComparison 
            ? 'border-purple-300 border-2 shadow-purple-100' 
            : 'border-gray-200 hover:shadow-md'
      } transition-all duration-200 group relative h-full flex flex-col`}>
        
        {/* Indicador de producto no disponible en esquina superior izquierda */}
        {!isProductAvailable && (
          <div 
            className="absolute top-2 left-2 z-10 p-1 bg-gray-500 text-white rounded-full"
            title={getUnavailableTooltip()}
          >
            <CalendarX className="h-3 w-3" />
          </div>
        )}
        
        {/* Badges container (sin descuento — descuento ahora va al lado del precio final) */}
        <div className={`absolute top-2 ${!isProductAvailable ? 'left-12' : 'left-2'} z-10 flex items-center space-x-2`}>
          {/* Badge de buybox */}
          {hasBuybox && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setBuyboxModalOpen(true);
              }}
              className="flex items-center h-7 px-2 rounded-md shadow transform hover:scale-105 transition-all cursor-pointer bg-gradient-to-r from-yellow-400 to-yellow-500 text-white hover:from-yellow-500 hover:to-yellow-600"
              title={t('buybox.winner_tooltip', 'Ganador del Buybox')}
            >
              <div className="flex items-center space-x-1">
                <Trophy className="h-3 w-3" />
                <span className="text-xs font-semibold">
                  {buyboxVariantsCount > 0 ? buyboxVariantsCount : '...'}
                </span>
              </div>
            </button>
          )}
        </div>
        
        {/* Botón de comparación en esquina superior derecha */}
        {(onAddToComparison || onRemoveFromComparison) && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleComparisonToggle();
            }}
            className={`absolute top-2 right-2 z-10 p-1 rounded-full transition-all duration-200 ${
              comparisonMode || isReallyInComparison
                ? 'bg-purple-400 text-white hover:bg-purple-500 transform scale-110'
                : 'bg-white text-purple-400 hover:bg-gray-50 hover:text-purple-500 border border-purple-200'
            }`}
            title={
              comparisonMode 
                ? t('compare.remove_from_comparison') 
                : isReallyInComparison 
                  ? t('compare.remove_from_comparison') 
                  : t('compare.add_to_compare')
            }
          >
            {comparisonMode ? (
              <X className="h-2.5 w-2.5" />
            ) : (
              <GitCompare className="h-2.5 w-2.5" />
            )}
          </button>
        )}

        {/* Imagen del producto */}
        <div className="aspect-square relative bg-gray-100 overflow-hidden group">
          {getProductImage() ? (
            <>
              <img
                src={getProductImage()}
                alt={getProductTitle()}
                loading="lazy"
                className={`w-full h-full object-cover cursor-pointer hover:scale-105 transition-all duration-300 ${
                  !isProductAvailable 
                    ? 'opacity-60 grayscale group-hover:grayscale-0 group-hover:opacity-100' 
                    : ''
                }`}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const placeholder = target.parentElement?.querySelector('.image-placeholder');
                  if (placeholder) {
                    placeholder.classList.remove('hidden');
                  }
                }}
                onClick={() => openImageModal(getProductImage(), getProductTitle())}
              />
              {/* Placeholder cuando falla la carga */}
              <div className="image-placeholder absolute inset-0 flex items-center justify-center text-gray-400 hidden">
                <div className="text-center">
                  <Eye className="h-12 w-12 mx-auto mb-2" />
                  <span className="text-sm">{t('product.no_image', 'Sin imagen')}</span>
                </div>
              </div>
            </>
          ) : (
            /* Placeholder cuando no hay imagen */
            <div className="absolute inset-0 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <Eye className="h-12 w-12 mx-auto mb-2" />
                <span className="text-sm">{t('product.no_image', 'Sin imagen')}</span>
              </div>
            </div>
          )}

          {/* Botones superpuestos */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
            <div className="flex space-x-2">
              {getProductImage() && (
                <button
                  onClick={() => openImageModal(getProductImage(), getProductTitle())}
                  className="bg-white text-gray-700 p-2 rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                  title={t('product.view_larger_image')}
                >
                  <Eye className="h-4 w-4" />
                </button>
              )}
              
              {getProductUrl() && (
                <a
                  href={getProductUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-blue-600 p-2 rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                  title={t('product.view_on_site')}
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Contenido de la tarjeta */}
        <div className="p-4 flex-1 flex flex-col">
          {/* Marca */}
          <div className="mb-2">
            <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">
              {getProductBrand()}
            </span>
          </div>

          {/* Título del producto */}
          <h3 className="text-sm font-medium text-gray-900 mb-2 line-clamp-2 leading-relaxed min-h-[2.5rem]" title={getProductTitle()}>
            {getProductTitle()}
          </h3>

          {/* Categoría */}
          <div className="mb-2 min-h-[1rem]">
            <span className="text-xs text-gray-500">
              {getProductCategory() || '\u00A0'}
            </span>
          </div>

          {/* Precio */}
          <div className="mb-2">
            <div className="flex flex-col space-y-1">
              <div className="min-h-[1.5rem]">
                {hasDiscount && (
                  <span className="text-base text-gray-500" style={{ textDecoration: 'line-through', textDecorationStyle: 'dotted' }}>
                    ${price.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-2xl font-bold ${getPriceColor()}`}>
                  ${finalPrice.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
                {/* Badge de descuento — mismo diseño que tenía como overlay sobre imagen */}
                {hasDiscount && discountPercentage > 0 && (
                  <div className="flex items-center h-7 bg-red-600 text-white px-2 rounded-md shadow transform hover:scale-105 transition-transform">
                    <span className="text-xs font-semibold">
                      {isRealDiscount ? `-${discountPercentage}%` : `${discountPercentage}%`}
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Cuotas */}
            <div className="text-xs text-gray-600 mt-1 min-h-[1rem]">
              {cuotas && precioCuota && cuotas !== 'null' && precioCuota !== 'null' && !isNaN(parseFloat(precioCuota)) ? (
                <span>
                  {cuotas} {t('product.installments')} ${parseFloat(precioCuota).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
              ) : (
                '\u00A0'
              )}
            </div>
          </div>

          {/* Separador y información del sitio */}
          <div className="border-t border-gray-100 pt-2 mt-auto">
            <div className="flex items-center justify-between">
              {/* Sitio */}
              <span className="text-xs text-gray-500 flex-1 min-w-0 truncate">
                {getProductSite() || ''}
              </span>
              
              {/* Página y Orden o Múltiples apariciones */}
              <div className="flex items-center space-x-2 text-xs text-gray-400 flex-shrink-0">
                {hasMultipleApariciones ? (
                  <div className="relative group/tooltip">
                    <div className="flex items-center space-x-1 cursor-help">
                      <List className="h-4 w-4" />
                      <span className="text-xs">{apariciones.length}</span>
                    </div>
                    {/* Tooltip con todas las apariciones */}
                    <div className="absolute bottom-full right-0 mb-2 invisible group-hover/tooltip:visible opacity-0 group-hover/tooltip:opacity-100 transition-all duration-200 z-50">
                      <div className="bg-gray-800 text-white p-2 rounded-lg shadow-lg min-w-[200px] max-w-[300px]">
                        <div className="text-xs font-semibold mb-1 border-b border-gray-600 pb-1">
                          Múltiples apariciones ({apariciones.length})
                        </div>
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {apariciones.map((ap: any, idx: number) => (
                            <div key={idx} className="text-xs">
                              <span className="font-medium">
                                {ap.category_toma || 'Sin categoría'}:
                              </span>{' '}
                              <span className="text-gray-300">
                                Pág. {String(ap.page || 0).padStart(2, '0')}, 
                                Pos. {String(ap.orderInPage || 0).padStart(2, '0')}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <span className={getProductPage() !== '--' ? '' : 'opacity-30'}>
                      p.{getProductPage()}
                    </span>
                    <span className={getProductOrder() !== '--' ? '' : 'opacity-30'}>
                      o.{getProductOrder()}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de imagen */}
      <ImageModal
        isOpen={imageModalOpen}
        onClose={closeImageModal}
        imageUrl={selectedImage?.url || ''}
        productTitle={selectedImage?.title || ''}
      />
      
      {/* Modal de buybox */}
      <BuyboxModal
        isOpen={buyboxModalOpen}
        onClose={() => setBuyboxModalOpen(false)}
        buyboxData={{
          buybox_id: buyboxId,
          tiene_buybox: isBuyboxWinner ? 1 : 0,
          posicion_actual: 1,
          fecha_captura: new Date().toISOString(),
          precio_venta: finalPrice,
          otras_posiciones: []
        }}
        productData={product}
        tableName={product.originalTableName || tableName}
      />
    </>
  );
};

export default ProductCard; 